import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";

import { ACCOUNT_BLOCKED_PATH, canAccessSystem } from "@/features/auth/lib/system-access";
import { isTenantSignOutInProgress } from "@/features/auth/hooks/use-app-sign-out";
import { deactivateCurrentDevicePush } from "@/features/push";
import { useAuthContextStore } from "@/features/auth/services/auth-context-store";
import { clearUserBranchAccessPersist } from "@/features/auth/services/user-branch-access-store";
import { buildLoginRedirectPath, normalizeTenantPathname } from "@/shared/lib/routing/app-route-scope";
import { setHttpForbiddenHandler, setHttpUnauthorizedHandler } from "@/shared/lib/api/http";
import { refreshAuthMeContext } from "@/features/auth/services/auth-query";

export function AuthAccessSync() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const forbiddenRefreshRef = useRef<Promise<unknown> | null>(null);
  const { pathname } = useLocation();
  const clerk = useClerk();
  const { isLoaded, isSignedIn } = useAuth();
  const shouldSyncAuthContext = isLoaded && isSignedIn;
  const setAuthMe = useAuthContextStore((state) => state.setAuthMe);
  const setActiveWorkspace = useAuthContextStore((state) => state.setActiveWorkspace);
  const setHandlingUnauthorized = useAuthContextStore((state) => state.setHandlingUnauthorized);
  const setAuthMeAttempted = useAuthContextStore((state) => state.setAuthMeAttempted);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    setHandlingUnauthorized(false);
  }, [isLoaded, isSignedIn, setHandlingUnauthorized]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    setAuthMeAttempted(false);
    setHandlingUnauthorized(false);
    setAuthMe(null);
    setActiveWorkspace(null, null);
    clearUserBranchAccessPersist();
  }, [isLoaded, isSignedIn, setActiveWorkspace, setAuthMe, setAuthMeAttempted, setHandlingUnauthorized]);

  useEffect(() => {
    if (!shouldSyncAuthContext) {
      setHttpUnauthorizedHandler(null);
      setHttpForbiddenHandler(null);
      return;
    }

    async function redirectToLogin() {
      const isSignOutTransition = isTenantSignOutInProgress();
      const next = isSignOutTransition
        ? "/login"
        : typeof window !== "undefined"
          ? buildLoginRedirectPath(normalizeTenantPathname(window.location.pathname), window.location.search)
          : buildLoginRedirectPath(normalizeTenantPathname(pathname ?? "/"), "");

      setHandlingUnauthorized(true);
      setAuthMe(null);
      setActiveWorkspace(null, null);
      clearUserBranchAccessPersist();
      // Free this browser's push channel for the next user (best-effort; unsubscribes locally even if the API
      // call 401s on an already-dead session).
      await deactivateCurrentDevicePush();
      await clerk.signOut().catch(() => null);
      navigate(next, { replace: true });
    }

    setHttpUnauthorizedHandler(async () => {
      await redirectToLogin();
    });

    setHttpForbiddenHandler(async () => {
        // A 403 can be the first observable sign that an administrator revoked this session's role from
        // another browser. Refresh the authoritative permission graph before the next render so stale menus,
        // routes and buttons disappear immediately. Coalesce simultaneous failed requests into one refresh.
        if (!forbiddenRefreshRef.current) {
          const refresh = refreshAuthMeContext(queryClient)
            .catch(() => null)
            .finally(() => {
              forbiddenRefreshRef.current = null;
            });
          forbiddenRefreshRef.current = refresh;
          await refresh;
        }

        const currentStatus = useAuthContextStore.getState().authMe?.user?.status;
        if (!canAccessSystem(currentStatus)) {
          if (pathname !== ACCOUNT_BLOCKED_PATH) {
            navigate(ACCOUNT_BLOCKED_PATH, { replace: true });
          }
          return true;
        }

      return false;
    });

    return () => {
      setHttpUnauthorizedHandler(null);
      setHttpForbiddenHandler(null);
      setHandlingUnauthorized(false);
    };
  }, [
    clerk,
    pathname,
    navigate,
    queryClient,
    setActiveWorkspace,
    setAuthMe,
    setHandlingUnauthorized,
    shouldSyncAuthContext,
  ]);

  return null;
}
